import {
  AGENT_PAGE_ICON_KEYS,
  type AgentPageIconKey,
  type AgentPageNavGroup,
} from '@erp/shared-types';
import type {
  AgentPageResponse,
  CreateAgentPageInput,
  UpdateAgentPageInput,
} from '@erp/rpc';
import { toNavGroup } from 'src/components/task-pages/pagePlacement';
import { parseSortOrder } from '../task-pages/taskPageAdmin';

/**
 * Pure draft state + validation for the Agent Pages admin dialog, kept free
 * of React imports so it stays trivially unit-testable (mirrors
 * taskPageAdmin.ts).
 */

export type AgentPageDraft = {
  /** Null when creating; the page id when editing. */
  id: string | null;
  name: string;
  description: string;
  icon: AgentPageIconKey | null;
  agentDefinitionId: string;
  /** Built-in sidebar section; null with a null pageSectionId = "Pages". */
  navGroup: AgentPageNavGroup | null;
  /** Org-created page section; set exclusively with `navGroup`. */
  pageSectionId: string | null;
  /** Kept as the raw input string; validated by {@link parseSortOrder}. */
  sortOrder: string;
  audienceUserGroupIds: string[];
};

export function emptyAgentPageDraft(): AgentPageDraft {
  return {
    id: null,
    name: '',
    description: '',
    icon: 'robot',
    agentDefinitionId: '',
    navGroup: null,
    pageSectionId: null,
    sortOrder: '0',
    audienceUserGroupIds: [],
  };
}

function toIconKey(value: string | null): AgentPageIconKey | null {
  return AGENT_PAGE_ICON_KEYS.find((key) => key === value) ?? null;
}

export function draftFromAgentPage(page: AgentPageResponse): AgentPageDraft {
  return {
    id: page.id,
    name: page.name,
    description: page.description ?? '',
    icon: toIconKey(page.icon),
    agentDefinitionId: page.agentDefinitionId,
    navGroup: toNavGroup(page.navGroup),
    pageSectionId: page.pageSectionId,
    sortOrder: String(page.sortOrder),
    audienceUserGroupIds: [...page.audienceUserGroupIds],
  };
}

export function isAgentPageDraftValid(draft: AgentPageDraft): boolean {
  return (
    draft.name.trim().length > 0 &&
    draft.agentDefinitionId.length > 0 &&
    parseSortOrder(draft.sortOrder) !== null
  );
}

export function isAgentPageDraftDirty(
  draft: AgentPageDraft,
  initial: AgentPageDraft,
): boolean {
  return JSON.stringify(draft) !== JSON.stringify(initial);
}

export function draftToCreateInput(
  draft: AgentPageDraft,
): CreateAgentPageInput {
  return {
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    icon: draft.icon,
    agentDefinitionId: draft.agentDefinitionId,
    navGroup: draft.navGroup,
    pageSectionId: draft.pageSectionId,
    sortOrder: parseSortOrder(draft.sortOrder) ?? 0,
    audienceUserGroupIds: draft.audienceUserGroupIds,
  };
}

export function draftToUpdateInput(
  draft: AgentPageDraft,
): UpdateAgentPageInput | null {
  if (!draft.id) return null;
  return { id: draft.id, ...draftToCreateInput(draft) };
}
